<template>
  <div class="print-generator-wrapper" @mouseup="stopDrag" @mouseleave="stopDrag" @mousemove="onDrag">
    <iframe ref="printFrame" style="position: absolute; width: 0; height: 0; border: none; visibility: hidden"></iframe>

    <v-container fluid class="pa-0 full-height-container">
      <v-row class="fill-height ma-0">
        <v-col cols="12" md="4" lg="3" class="editor-sidebar pa-0">
          <v-card tile flat class="d-flex flex-column fill-height grey darken-4">
            <div class="pa-4 overflow-y-auto flex-grow-1 custom-scrollbar">
              <div class="section-label mb-2 mt-2">PAPIERFORMAT</div>
              <v-btn-toggle v-model="config.format" mandatory class="mb-2 d-flex w-100" dark dense color="primary" background-color="grey darken-3">
                <v-btn value="a5" class="flex-grow-1">A5</v-btn>
                <v-btn value="a4" class="flex-grow-1">A4</v-btn>
                <v-btn value="a3" class="flex-grow-1">A3</v-btn>
              </v-btn-toggle>
              <v-btn-toggle
                v-model="config.orientation"
                mandatory
                class="mb-6 d-flex w-100"
                dark
                dense
                color="primary"
                background-color="grey darken-3">
                <v-btn value="portrait" class="flex-grow-1"><v-icon small left>mdi-crop-portrait</v-icon> Hoch</v-btn>
                <v-btn value="landscape" class="flex-grow-1"><v-icon small left>mdi-crop-landscape</v-icon> Quer</v-btn>
              </v-btn-toggle>

              <v-divider class="grey darken-3 mb-6"></v-divider>

              <div class="element-box mb-3" :class="{ active: selectedElement === 'text1' }" @click="selectedElement = 'text1'">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="caption font-weight-bold primary--text">1. TITEL (GROSS)</div>
                  <div class="caption grey--text">{{ style.text1.size }}pt</div>
                </div>
                <v-textarea
                  v-model="content.text1"
                  rows="2"
                  auto-grow
                  dense
                  outlined
                  dark
                  hide-details
                  placeholder="Titel"
                  class="mb-2 input-dense"></v-textarea>

                <div v-if="selectedElement === 'text1'">
                  <div class="d-flex align-center mt-2">
                    <v-icon small class="mr-2 grey--text">mdi-format-size</v-icon>
                    <v-slider v-model="style.text1.size" :min="style.text2.size + 5" max="120" dense hide-details class="align-center"></v-slider>
                  </div>
                  <div class="color-dots mt-2">
                    <div v-for="c in colors" :key="c" class="dot" :style="{ background: c }" @click.stop="style.text1.color = c"></div>
                  </div>
                </div>
              </div>

              <div class="element-box mb-3" :class="{ active: selectedElement === 'text2' }" @click="selectedElement = 'text2'">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="caption font-weight-bold white--text">2. SUBTITEL (MITTEL)</div>
                  <div class="caption grey--text">{{ style.text2.size }}pt</div>
                </div>
                <v-textarea
                  v-model="content.text2"
                  rows="3"
                  auto-grow
                  dense
                  outlined
                  dark
                  hide-details
                  placeholder="Haupttext"
                  class="mb-2 input-dense"></v-textarea>

                <div v-if="selectedElement === 'text2'">
                  <div class="d-flex align-center mt-2">
                    <v-icon small class="mr-2 grey--text">mdi-format-size</v-icon>
                    <v-slider
                      v-model="style.text2.size"
                      :min="style.text3.size + 5"
                      :max="style.text1.size - 5"
                      dense
                      hide-details
                      class="align-center"></v-slider>
                  </div>
                  <div class="color-dots mt-2">
                    <div v-for="c in colors" :key="c" class="dot" :style="{ background: c }" @click.stop="style.text2.color = c"></div>
                  </div>
                </div>
              </div>

              <div class="element-box mb-6" :class="{ active: selectedElement === 'text3' }" @click="selectedElement = 'text3'">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="caption font-weight-bold grey--text">3. INFO (KLEIN - OPTIONAL)</div>
                  <div class="caption grey--text">{{ style.text3.size }}pt</div>
                </div>
                <v-text-field
                  v-model="content.text3"
                  dense
                  outlined
                  dark
                  hide-details
                  placeholder="Zusatzinfo (optional)"
                  class="mb-2 input-dense"></v-text-field>

                <div v-if="selectedElement === 'text3'">
                  <div class="d-flex align-center mt-2">
                    <v-icon small class="mr-2 grey--text">mdi-format-size</v-icon>
                    <v-slider v-model="style.text3.size" min="10" :max="style.text2.size - 5" dense hide-details class="align-center"></v-slider>
                  </div>
                  <div class="color-dots mt-2">
                    <div v-for="c in colors" :key="c" class="dot" :style="{ background: c }" @click.stop="style.text3.color = c"></div>
                  </div>
                </div>
              </div>

              <v-divider class="grey darken-3 mb-6"></v-divider>

              <div class="section-label mb-2">GRAFIKEN</div>

              <div class="d-flex align-center mb-3">
                <v-btn color="grey darken-3" class="flex-grow-1 mr-2" dark depressed small @click="$refs.fileInput.click()">
                  <v-icon left small>mdi-image-plus</v-icon> Hauptbild
                </v-btn>
                <v-btn v-if="content.image" icon color="red" small @click="removeImage">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
                <input type="file" ref="fileInput" style="display: none" accept="image/*" @change="onFileChange" />
              </div>

              <v-btn block color="grey darken-3" dark depressed small @click="logoDialog = true" class="mb-2">
                <v-icon left small>mdi-sticker-emoji</v-icon> Logo wählen
              </v-btn>
            </div>

            <div class="pa-4 grey darken-3">
              <v-btn block color="#00943c" large dark depressed @click="print" :loading="isPrinting">
                <v-icon left>mdi-printer</v-icon> Drucken
              </v-btn>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="8" lg="9" class="preview-area d-flex align-center justify-center">
          <div class="paper-scaler" :style="{ transform: `scale(${previewScale})` }">
            <div class="paper" :class="[config.format, config.orientation]" ref="paper" id="printable-area">
              <div class="image-stripe-bg"></div>
              <div class="image-shape-container">
                <div
                  class="image-fill"
                  :style="
                    content.image
                      ? {
                          backgroundImage: 'url(' + content.image + ')',
                          backgroundPosition: imgPos.x + '% ' + imgPos.y + '%',
                        }
                      : {}
                  "
                  @mousedown.stop="startDrag($event, 'image')"
                  :class="{ 'cursor-move': content.image }">
                  <div v-if="!content.image" class="placeholder-text">BILD</div>
                  <div class="image-drag-hint" v-if="content.image">
                    <v-icon color="white">mdi-cursor-move</v-icon>
                  </div>
                </div>
              </div>

              <img :src="content.logo" class="header-logo" alt="Logo" />

              <div
                class="draggable-text text-level-1"
                :class="{ 'is-active': selectedElement === 'text1' }"
                :style="getTextStyle('text1')"
                @mousedown.stop="startDrag($event, 'text1')">
                {{ content.text1 || "TITEL" }}
                <div class="drag-guide"><v-icon x-small>mdi-arrow-up-down</v-icon></div>
              </div>

              <div
                class="draggable-text text-level-2"
                :class="{ 'is-active': selectedElement === 'text2' }"
                :style="getTextStyle('text2')"
                @mousedown.stop="startDrag($event, 'text2')">
                {{ content.text2 || "Hier steht dein Haupttext." }}
                <div class="drag-guide"><v-icon x-small>mdi-arrow-up-down</v-icon></div>
              </div>

              <div
                v-if="content.text3"
                class="draggable-text text-level-3"
                :class="{ 'is-active': selectedElement === 'text3' }"
                :style="getTextStyle('text3')"
                @mousedown.stop="startDrag($event, 'text3')">
                {{ content.text3 }}
                <div class="drag-guide"><v-icon x-small>mdi-arrow-up-down</v-icon></div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="logoDialog" max-width="600">
      <v-card dark>
        <v-card-title>Logo wählen</v-card-title>
        <v-card-text>
          <v-container fluid>
            <v-row>
              <v-col cols="6" sm="4" v-for="(logo, index) in availableLogos" :key="index">
                <v-card
                  hover
                  @click="setLogo(logo)"
                  :class="{ 'active-logo': content.logo === logo.path }"
                  class="pa-2 white d-flex align-center justify-center"
                  height="120">
                  <img :src="logo.path" style="max-width: 100%; max-height: 100%; object-fit: contain" />
                </v-card>
                <div class="caption text-center mt-1 grey--text">{{ logo.name }}</div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="logoDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import userService from "@/services/userService"

export default {
  name: "CommunicationDashboardPrintGenerator",
  data() {
    return {
      userService,
      isPrinting: false,
      previewScale: 0.5,
      logoDialog: false,

      dragState: {
        active: false,
        element: null,
        startY: 0,
        startX: 0,
        startPosX: 0,
        startPosY: 0,
      },
      selectedElement: "text1",

      config: { format: "a4", orientation: "portrait" },

      content: {
        text1: "",
        text2: "",
        text3: "",
        image: null,
        logo: "/img/logos/studio/WELL-COME-FIT_LOGO_WHITE.png",
        logoName: "Standard",
      },

      pos: {
        text1: { y: 35 },
        text2: { y: 55 },
        text3: { y: 80 },
      },

      imgPos: { x: 50, y: 50 },

      style: {
        text1: { size: 60, color: "#00943c" },
        text2: { size: 30, color: "#000000" },
        text3: { size: 18, color: "#000000" },
      },

      colors: ["#000000", "#333333", "#00943c", "#FFFFFF", "#E63946", "#1D3557"],
    }
  },
  mounted() {
    window.addEventListener("resize", this.calculateScale)
    this.calculateScale()
    setTimeout(this.calculateScale, 100)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateScale)
  },
  watch: {
    "config.format": function () {
      this.$nextTick(this.calculateScale)
    },
    "config.orientation": function () {
      this.$nextTick(this.calculateScale)
    },
    "style.text1.size"(val) {
      if (this.style.text2.size >= val) this.style.text2.size = val - 5
    },
    "style.text2.size"(val) {
      if (this.style.text3.size >= val) this.style.text3.size = val - 5
      if (val >= this.style.text1.size) this.style.text1.size = val + 5
    },
    "style.text3.size"(val) {
      if (val >= this.style.text2.size) this.style.text2.size = val + 5
    },
  },
  computed: {
    availableLogos() {
      let logos = [{ name: "Standard", path: "/img/logos/studio/WELL-COME-FIT_LOGO_WHITE.png" }]

      if (this.userService && this.userService.getStudio()) {
        logos.push({
          name: "studioLogo",
          path: "https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_" + this.userService.getStudio().code.toUpperCase() + "_WHITE.png",
        })
      }
      return logos
    },
  },
  methods: {
    // --- DYNAMISCHE POSITIONIERUNG ---
    getTextStyle(element) {
      const y = this.pos[element].y
      const currentImageWidth = 46 - y * 0.25
      let margin = 5
      if (element === "text1") margin = 2
      const left = currentImageWidth + margin
      return {
        top: y + "%",
        left: left + "%",
        width: 100 - left - 5 + "%",
        color: this.style[element].color,
        fontSize: this.style[element].size + "pt",
        // Hier sicherstellen, dass auch in der Vorschau der Font genutzt wird,
        // falls du ihn global im CSS hast. Für Druck ist print() entscheidend.
        fontFamily: "Saira BlackItalic, sans-serif",
        fontStyle: "italic",
      }
    },

    setLogo(logo) {
      this.content.logo = logo.path
      this.content.logoName = logo.name
      this.logoDialog = false
    },

    calculateScale() {
      if (!this.$refs.paper) return
      const container = this.$el.querySelector(".preview-area")
      if (!container) return
      const availableW = container.clientWidth - 40
      const availableH = container.clientHeight - 40
      const paperW = this.$refs.paper.clientWidth
      const paperH = this.$refs.paper.clientHeight
      const scaleW = availableW / paperW
      const scaleH = availableH / paperH
      this.previewScale = Math.min(scaleW, scaleH, 1)
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        this.content.image = e.target.result
        this.imgPos = { x: 50, y: 50 }
      }
      reader.readAsDataURL(file)
    },
    removeImage() {
      this.content.image = null
      this.$refs.fileInput.value = ""
    },

    startDrag(e, element) {
      if (element === "image" && !this.content.image) return
      this.selectedElement = element
      this.dragState.active = true
      this.dragState.element = element
      this.dragState.startX = e.clientX
      this.dragState.startY = e.clientY

      if (element === "image") {
        this.dragState.startPosX = this.imgPos.x
        this.dragState.startPosY = this.imgPos.y
      } else {
        this.dragState.startPosY = this.pos[element].y
      }
    },

    onDrag(e) {
      if (!this.dragState.active) return
      e.preventDefault()

      const el = this.dragState.element

      if (el === "image") {
        const sensitivity = 0.2
        const dx = (e.clientX - this.dragState.startX) * sensitivity
        const dy = (e.clientY - this.dragState.startY) * sensitivity
        let newX = this.dragState.startPosX - dx
        let newY = this.dragState.startPosY - dy
        if (newX < 0) newX = 0
        if (newX > 100) newX = 100
        if (newY < 0) newY = 0
        if (newY > 100) newY = 100
        this.imgPos = { x: newX, y: newY }
      } else {
        const paper = this.$refs.paper
        const dy = e.clientY - this.dragState.startY
        const dyPercent = (dy / (paper.clientHeight * this.previewScale)) * 100
        let newY = this.dragState.startPosY + dyPercent

        const buffer = 5
        let minY = 5
        let maxY = 95

        if (el === "text1") {
          maxY = this.pos.text2.y - buffer
        } else if (el === "text2") {
          minY = this.pos.text1.y + buffer
          if (this.content.text3) maxY = this.pos.text3.y - buffer
        } else if (el === "text3") {
          minY = this.pos.text2.y + buffer
        }

        if (newY < minY) newY = minY
        if (newY > maxY) newY = maxY

        this.pos[el].y = newY
      }
    },

    async stopDrag() {
      this.dragState.active = false
    },

    print() {
      this.stopDrag()
      this.isPrinting = true
      this.selectedElement = null

      const paperElement = this.$refs.paper
      const iframe = this.$refs.printFrame

      const getPrintLeft = (y) => 46 - y * 0.25 + 5 + "%"
      const getPrintWidth = (y) => 100 - (50 - y * 0.25 + 5) - 5 + "%"

      const pageSizeCSS = `${this.config.format} ${this.config.orientation}`
      // HIER IST DER FIX: @font-face direkt in den String einfügen
      // Wichtig: 'src: url(/fonts/...)' funktioniert nur, wenn die Datei in public/fonts liegt!
      const cssStyles = `
        @font-face {
          font-family: 'Saira BlackItalic';
          src: url('/fonts/Saira-BlackItalic.ttf') format('truetype');
          font-weight: bold;
          font-style: italic;
        }
        
        @page { 
            margin: 0; 
            size: ${pageSizeCSS}; 
        }        
        /* Font Family hier anwenden */
        body { 
            margin: 0; 
            padding: 0; 
            font-family: 'Saira BlackItalic', sans-serif; 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact; 
        }
        
        .paper { position: relative; overflow: hidden; box-shadow: none; width: 100%; height: 100vh; }

        .paper.a4.portrait  { width: 210mm; height: 297mm; }
        .paper.a4.landscape { width: 297mm; height: 210mm; }
        .paper.a3.portrait  { width: 297mm; height: 420mm; }
        .paper.a3.landscape { width: 420mm; height: 297mm; }
        .paper.a5.portrait  { width: 148mm; height: 210mm; }
        .paper.a5.landscape { width: 210mm; height: 148mm; }

        .image-stripe-bg {
          position: absolute; top: 0; left: 0; height: 100%;
          width: 50.5%;
          clip-path: polygon(0 0, 100% 0, 50% 100%, 0% 100%); 
          background: #00943c !important;
          z-index: 0;
        }
        
        .image-shape-container {
          position: absolute; top: 0; left: 0; height: 100%;
          width: 50%;
          clip-path: polygon(0 0, 100% 0, 50% 100%, 0% 100%);
          background: #f0f0f0 !important;
          z-index: 1;
        }

        .image-fill { width: 100%; height: 100%; background-size: cover; background-repeat: no-repeat; }

        .header-logo { 
            position: absolute; 
            top: 7%; 
            left: 48%; 
            width: 20%; 
            z-index: 10; 
            object-fit: contain; 
        }
        
        .draggable-text { 
            position: absolute; 
            z-index: 20; 
            line-height: 1.2; 
            white-space: pre-wrap; 
            font-style: italic; /* Da Font schon Italic ist, könnte man das auch weglassen, aber sicher ist sicher */
        }
        
        .text-level-1 { 
          left: ${getPrintLeft(this.pos.text1.y) - 1}; 
          width: ${getPrintWidth(this.pos.text1.y)};
          font-weight: 900; 
          text-transform: uppercase; 
        }
        .text-level-2 { 
          left: ${getPrintLeft(this.pos.text2.y)}; 
          width: ${getPrintWidth(this.pos.text2.y)};
          font-weight: 700; 
        }
        .text-level-3 { 
          left: ${getPrintLeft(this.pos.text3.y)}; 
          width: ${getPrintWidth(this.pos.text3.y)};
          font-weight: 500; 
        }

        .is-active, .drag-guide, .placeholder-text, .image-drag-hint { display: none !important; }
      `

      const doc = iframe.contentWindow.document
      doc.open()
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Drucken</title>
            <style>${cssStyles}</style>
          </head>
          <body>
            ${paperElement.outerHTML}
          </body>
        </html>
      `)
      doc.close()

      setTimeout(() => {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
        this.isPrinting = false
      }, 500)
    },
  },
}
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Saira BlackItalic";
  src: url("/fonts/Saira-BlackItalic.ttf") format("truetype");
}
.full-height-container {
  height: calc(100vh - 64px);
  overflow: hidden;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 3px;
}
.section-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #666;
}
.element-box {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid transparent;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}
.element-box:hover {
  background: rgba(255, 255, 255, 0.05);
}
.element-box.active {
  background: rgba(0, 148, 60, 0.1);
  border-color: #00943c;
}
.color-dots {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #555;
  cursor: pointer;
}
.dot:hover {
  transform: scale(1.1);
}
.input-dense >>> .v-input__slot {
  min-height: 0 !important;
}
.preview-area {
  background: #1e1e1e;
  overflow: hidden;
  position: relative;
  height: 100%;
}
.paper-scaler {
  transform-origin: center center;
  transition: transform 0.2s ease-out;
  display: flex;
  justify-content: center;
  align-items: center;
}
.paper {
  background: white;
  position: relative;
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  font-family: "Saira BlackItalic", sans-serif !important; /* HIER ANWENDEN */
  font-style: italic;
  flex-shrink: 0;
}
.paper.a4.portrait {
  width: 210mm;
  height: 297mm;
}
.paper.a4.landscape {
  width: 297mm;
  height: 210mm;
}
.paper.a3.portrait {
  width: 297mm;
  height: 420mm;
}
.paper.a3.landscape {
  width: 420mm;
  height: 297mm;
}
.paper.a5.portrait {
  width: 148mm;
  height: 210mm;
}
.paper.a5.landscape {
  width: 210mm;
  height: 148mm;
}

/* 50% Oben, 25% Unten */
.image-stripe-bg {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 50.5%;
  clip-path: polygon(0 0, 100% 0, 50% 100%, 0% 100%);
  background: #00943c;
  z-index: 0;
}
.image-shape-container {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 50%;
  clip-path: polygon(0 0, 100% 0, 50% 100%, 0% 100%);
  background: #f0f0f0;
  z-index: 1;
  pointer-events: none;
}

.image-fill {
  width: 100%;
  height: 100%;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}
.cursor-move {
  cursor: move;
}
.image-drag-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.3);
  padding: 10px;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}
.image-fill:hover .image-drag-hint {
  opacity: 1;
}
.placeholder-text {
  font-size: 40pt;
  font-weight: 900;
  color: rgba(0, 0, 0, 0.1);
  transform: rotate(-10deg);
  user-select: none;
}

.header-logo {
  position: absolute;
  top: 7%;
  left: 48%;
  width: 20%;
  z-index: 10;
  object-fit: contain;
}

.draggable-text {
  position: absolute;
  z-index: 20;
  cursor: ns-resize;
  white-space: pre-wrap;
  user-select: none;
  line-height: 1.2;
  font-style: italic;
  /* width und left werden jetzt inline gesetzt */
}
.draggable-text.is-active {
  outline: 2px dashed #00943c;
  background: rgba(0, 148, 60, 0.05);
}
.drag-guide {
  position: absolute;
  right: -25px;
  top: 50%;
  transform: translateY(-50%);
  color: #00943c;
  opacity: 0.5;
}
.text-level-1 {
  font-weight: 900;
  text-transform: uppercase;
}
.text-level-2 {
  font-weight: 700;
}
.text-level-3 {
  font-weight: 500;
}
.active-logo {
  border: 2px solid #00943c !important;
}
</style>
